import React, { lazy, Suspense, useMemo } from "react";
import { Chip } from "@mui/material";
import { Draggable } from "@hello-pangea/dnd";
import { AdjustedProductType, PolicyStatus } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { formatProductTypeUniversal } from "../../utils/productTypeUniversalFormatter";
import { colorProductTypeUniversal } from "../../utils/colorEnumFormatter";
import { formatNumberFloor } from "../../utils/namesFormater";

const ManualMimshakSelectionDialog = lazy(() => import("../Grow/Simulator/ManualMimshakSelectionDialog"));
const MimshakRowSelectionDialog = lazy(() => import("./MimshakRowSelectionDialog"));

interface AssetChipsPanelProps {
  contacts: Contact[];
  selectedPeriod: string;
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
  data: MimshakData[];
  manualAssetChips: MimshakData[];
  onAssetsAdded: (items: MimshakData[]) => void;
  onChipDelete: (index: number) => void;
  fundSumChipsLength: number;
  contactColorMap?: Record<number, string>;
  openManualDialog?: boolean;
  onManualDialogClose?: () => void;
  openExecutiveDialog?: boolean;
  onExecutiveDialogClose?: () => void;
}

const AssetChipsPanel: React.FC<AssetChipsPanelProps> = ({
  contacts,
  selectedPeriod,
  selectedPeriodType,
  data,
  manualAssetChips,
  onAssetsAdded,
  onChipDelete,
  fundSumChipsLength,
  contactColorMap,
  openManualDialog = false,
  onManualDialogClose,
  openExecutiveDialog = false,
  onExecutiveDialogClose,
}) => {
  const viewedType = contacts.length > 1 ? "FAMILY" : "USER";
  const contactIds = useMemo(() => contacts.map(c => c.id), [contacts]);

  const executiveInsuranceData = data.filter((item) => {
    if (
      item.adjusted_product_type === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
      item.adjusted_product_type === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE
    ) return true;
    if (
      item.adjusted_product_type === AdjustedProductType.MANDATORY_PENSION_FUND ||
      item.adjusted_product_type === AdjustedProductType.SUPPLEMENTARY_PENSION_FUND
    ) {
      const status = item.data?.products?.[0]?.policies_or_accounts?.[0]?.status;
      return status === PolicyStatus.NOT_ACTIVE;
    }
    return false;
  });

  return (
    <>
      {manualAssetChips.map((asset, index) => (
        <Draggable
          key={`manualAsset-${asset.id}-${index}`}
          draggableId={`fundSum-${asset.funds_sum}-transfer-from-${asset.policy_number}-cid-${asset.contact_id}`}
          index={fundSumChipsLength + index}
        >
          {(provided) => (
            <Chip
              label={`${formatProductTypeUniversal(asset.adjusted_product_type ?? asset.product_type)} ${formatNumberFloor(asset.funds_sum)}`}
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...{ ...provided.dragHandleProps, tabIndex: -1 }}
              onDelete={() => onChipDelete(index)}
              sx={{
                backgroundColor: colorProductTypeUniversal(asset.adjusted_product_type ?? asset.product_type),
                color: "white",
                fontWeight: "bold",
                ...(contactColorMap && asset.contact_id != null && contactColorMap[asset.contact_id] && {
                  borderRight: `8px solid ${contactColorMap[asset.contact_id]}`,
                }),
              }}
            />
          )}
        </Draggable>
      ))}

      <Suspense fallback={null}>
        <ManualMimshakSelectionDialog
          open={openManualDialog}
          onClose={() => onManualDialogClose?.()}
          contacts={contacts}
          viewedType={viewedType}
          onConfirm={onAssetsAdded}
          reportedPeriod={selectedPeriod}
        />
      </Suspense>
      <Suspense fallback={null}>
        <MimshakRowSelectionDialog
          open={openExecutiveDialog}
          onClose={() => onExecutiveDialogClose?.()}
          data={executiveInsuranceData}
          title="בחר נכסים מביטוח מנהלים / פנסיה לא פעילה"
          viewedType={viewedType}
          onConfirm={onAssetsAdded}
          reportedPeriod={selectedPeriod}
          contactIds={contactIds}
          selectedPeriodType={selectedPeriodType}
        />
      </Suspense>
    </>
  );
};

export default AssetChipsPanel;
